import { useState } from 'react'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { useLanguage } from '@/i18n/LanguageContext'
import { useAdminStats, useAdminClients, useCreateClient, useDeleteClient, useClientMediaFolders, useAdminUsers } from '@/api/hooks/useAdmin'
import { useSystemSettings } from '@/api/hooks/useSystemSettings'
import { BASE, getAuthHeaders, api } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { StatCard } from '@/components/shared/StatCard'
import { toast } from 'sonner'
import { Users, Instagram, Facebook, MessageCircle, UserPlus, Trash2, Shield, Zap, Lock, FolderOpen, Download, Loader2, AlertTriangle, Copy, CheckCircle2, RefreshCw, Server, Database, Play, Wrench } from 'lucide-react'

const ALL_PAGES = [
  { value: '/painel', label: 'Painel' },
  { value: '/chat', label: 'Chat' },
  { value: '/contatos', label: 'Contatos' },
  { value: '/broadcast', label: 'Broadcast' },
  { value: '/recuperar-comentarios', label: 'Recuperar Comentarios' },
  { value: '/ctm', label: 'Click-to-Messenger' },
  { value: '/sequencias', label: 'Sequencias' },
  { value: '/publicar', label: 'Publicar' },
  { value: '/midias', label: 'Midias' },
  { value: '/checker-bot', label: 'Checker Bot' },
  { value: '/financeiro', label: 'Financeiro' },
  { value: '/paginas', label: 'Paginas' },
  { value: '/automacao-fb', label: 'Automação FB' },
  { value: '/ad-manager', label: 'Ad Manager' },
  { value: '/emails', label: 'Emails' },
]

export function Admin() {
  const { t } = useLanguage()
  const queryClient = useQueryClient()
  const { data: stats, isLoading: statsLoading } = useAdminStats()
  const { data: clients, isLoading: clientsLoading } = useAdminClients()
  const { data: systemSettings } = useSystemSettings()
  const createClient = useCreateClient()
  const deleteClient = useDeleteClient()

  const toggleMaintenance = useMutation({
    mutationFn: (enabled: boolean) => api.put('/admin/system-settings', { maintenance_mode: enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['system-settings'] }),
  })

  // ─── Apps Meta (App ID + Chave Secreta + token) — postagem/broadcast separados ───
  type MetaAppInfo = { app_id: string | null; from_panel: boolean; has_secret: boolean; has_token: boolean; preview: string | null; status: string; checked_at: string | null; error: string | null }
  type MetaApiData = { apps: Record<'posting' | 'broadcast', MetaAppInfo>; webhook: { callback_url: string; verify_token: string | null } }
  const { data: metaApi } = useQuery<MetaApiData>({
    queryKey: ['admin', 'meta-api'],
    queryFn: () => api.get('/admin/meta-api'),
    refetchInterval: 60_000,
  })
  const [metaForm, setMetaForm] = useState<Record<'posting' | 'broadcast', { app_id: string; app_secret: string; token: string }>>({
    posting: { app_id: '', app_secret: '', token: '' },
    broadcast: { app_id: '', app_secret: '', token: '' },
  })
  const invalidateMeta = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'meta-api'] })
    queryClient.invalidateQueries({ queryKey: ['system-settings'] })
  }
  const saveMetaApp = useMutation({
    mutationFn: (app: 'posting' | 'broadcast') => {
      const f = metaForm[app]
      const payload: Record<string, string> = {}
      if (f.app_id.trim() !== '') payload[`meta_app_id_${app}`] = f.app_id.trim()
      if (f.app_secret.trim() !== '') payload[`meta_app_secret_${app}`] = f.app_secret.trim()
      if (f.token.trim() !== '') payload[`meta_master_token_${app}`] = f.token.trim()
      return api.put('/admin/system-settings', payload)
    },
    onSuccess: (_d, app) => {
      setMetaForm((s) => ({ ...s, [app]: { app_id: '', app_secret: '', token: '' } }))
      invalidateMeta()
    },
  })
  const testMetaApp = useMutation({
    mutationFn: (app: 'posting' | 'broadcast') => api.post('/admin/meta-api/test', { app }),
    onSuccess: () => invalidateMeta(),
  })
  const copyText = (label: string, text?: string | null) => {
    if (!text) return toast.error(t('Nada pra copiar'))
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} ${t('copiado')}`))
  }

  // ─── Workers / Filas (suporte resolve B.O. sem SSH) ───
  const { data: workers } = useQuery<{ pending: number; stuck: number; failed: number }>({
    queryKey: ['admin', 'workers'],
    queryFn: () => api.get('/admin/workers'),
    refetchInterval: 15_000,
  })
  const restartWorkers = useMutation({
    mutationFn: () => api.post('/admin/workers/restart', {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'workers'] }),
  })
  const clearWorkers = useMutation({
    mutationFn: () => api.post('/admin/workers/clear', {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'workers'] }),
  })
  const forceRestart = useMutation({
    mutationFn: () => api.post('/admin/workers/force-restart', {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'workers'] }),
  })

  // ─── Consulta ao banco (só leitura) ───
  const [sql, setSql] = useState('')
  const [dbResult, setDbResult] = useState<{ columns: string[]; rows: Record<string, unknown>[]; count: number } | null>(null)
  const runQuery = useMutation({
    mutationFn: () => api.post<{ columns?: string[]; rows?: Record<string, unknown>[]; count?: number; error?: string }>('/admin/db/query', { sql }),
    onSuccess: (r) => {
      if (r.error) { setDbResult(null); toast.error(r.error) }
      else { setDbResult({ columns: r.columns ?? [], rows: r.rows ?? [], count: r.count ?? 0 }) }
    },
    onError: () => toast.error(t('Erro ao consultar')),
  })

  // ─── Ações de suporte (correção dos B.O. recorrentes) ───
  const [postId, setPostId] = useState('')
  const [broadcastId, setBroadcastId] = useState('')
  const actionResult = (r: { ok?: boolean; message?: string; error?: string }) =>
    r.error ? toast.error(r.error) : toast.success(r.message || t('Feito'))
  const revalidateTokens = useMutation({
    mutationFn: () => api.post<{ ok?: boolean; message?: string; error?: string }>('/admin/actions/revalidate-tokens', {}),
    onSuccess: actionResult, onError: () => toast.error(t('Falha ao revalidar')),
  })
  const reprocessPost = useMutation({
    mutationFn: () => api.post<{ ok?: boolean; message?: string; error?: string }>('/admin/actions/reprocess-post', { id: postId.trim() }),
    onSuccess: (r) => { actionResult(r); if (r.ok) setPostId('') }, onError: () => toast.error(t('Falha ao reprocessar')),
  })
  const reprocessBroadcast = useMutation({
    mutationFn: () => api.post<{ ok?: boolean; message?: string; error?: string }>('/admin/actions/reprocess-broadcast', { id: broadcastId.trim() }),
    onSuccess: (r) => { actionResult(r); if (r.ok) setBroadcastId('') }, onError: () => toast.error(t('Falha ao reprocessar')),
  })

  const [createOpen, setCreateOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [newUsername, setNewUsername] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [restrictAccess, setRestrictAccess] = useState(false)
  const [selectedPages, setSelectedPages] = useState<string[]>(['/painel', '/publicar', '/midias'])

  // Library dialog state
  const [libraryClientId, setLibraryClientId] = useState<string | null>(null)
  const [libraryClientName, setLibraryClientName] = useState('')
  const { data: mediaData, isLoading: mediaLoading } = useClientMediaFolders(libraryClientId)
  const [downloadingFolder, setDownloadingFolder] = useState<string | null>(null)

  // Copy media state
  const { data: allUsers } = useAdminUsers()
  const [copySourceId, setCopySourceId] = useState('')
  const [copyProgress, setCopyProgress] = useState<{ phase: string; copied?: number; errors?: number; skipped?: number; total?: number; foldersCopied?: number } | null>(null)
  const [copyDone, setCopyDone] = useState<{ copied: number; errors: number; skipped: number; foldersCopied: number } | null>(null)

  const handleCopyMedia = () => {
    if (!libraryClientId || !copySourceId) return
    if (!confirm(`Copiar toda a biblioteca para ${libraryClientName}? Isso pode demorar.`)) return
    setCopyProgress({ phase: 'init' })
    setCopyDone(null)

    fetch(`${BASE}/admin/clients/${libraryClientId}/copy-media`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ sourceUserId: copySourceId }),
    }).then(async (res) => {
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Erro' }))
        toast.error(err.error || 'Erro ao copiar')
        setCopyProgress(null)
        return
      }
      const reader = res.body?.getReader()
      if (!reader) return
      const decoder = new TextDecoder()
      let buffer = ''
      let lastEvent = 'progress'

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const parts = buffer.split('\n')
        buffer = parts.pop() || ''
        for (const line of parts) {
          if (line.startsWith('event: ')) {
            lastEvent = line.slice(7).trim()
          } else if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6))
              if (lastEvent === 'done') {
                setCopyDone(data)
                setCopyProgress(null)
                queryClient.invalidateQueries({ queryKey: ['admin-client-media', libraryClientId] })
                toast.success(`Copiado! ${data.copied} arquivos, ${data.foldersCopied} pastas`)
              } else {
                setCopyProgress(data)
              }
            } catch {}
            lastEvent = 'progress'
          }
        }
      }
    }).catch(() => {
      toast.error('Erro na conexao')
      setCopyProgress(null)
    })
  }

  const handleCreate = () => {
    if (!newName.trim() || !newUsername.trim() || !newPassword) {
      toast.error(t('Preencha todos os campos'))
      return
    }
    if (newPassword.length < 6) {
      toast.error(t('Senha deve ter no minimo 6 caracteres'))
      return
    }
    createClient.mutate(
      {
        name: newName.trim(),
        username: newUsername.trim().toLowerCase(),
        password: newPassword,
        ...(restrictAccess ? { allowedPages: selectedPages } : {}),
      },
      {
        onSuccess: () => {
          toast.success(t('Cliente criado!'))
          setCreateOpen(false)
          setNewName('')
          setNewUsername('')
          setNewPassword('')
          setRestrictAccess(false)
          setSelectedPages(['/painel', '/publicar', '/midias'])
        },
        onError: (err) => toast.error(err.message || t('Erro ao criar')),
      },
    )
  }

  const handleDelete = (clientId: string, name: string) => {
    if (!confirm(`${t('Excluir')} "${name}"?`)) return
    deleteClient.mutate(clientId, {
      onSuccess: () => toast.success(t('Cliente excluido!')),
      onError: () => toast.error(t('Erro ao excluir')),
    })
  }

  const handleDownloadFolder = async (folderId: string, folderName: string) => {
    setDownloadingFolder(folderId)
    const toastId = toast.loading(`Compactando "${folderName}"...`)
    try {
      const res = await fetch(`${BASE}/admin/media-folders/${folderId}/download`, {
        headers: getAuthHeaders(),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Erro no download' }))
        throw new Error(err.error || 'Erro no download')
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${folderName}.zip`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      toast.success(`"${folderName}" baixado!`, { id: toastId })
    } catch (err: any) {
      toast.error(err.message || 'Erro ao baixar', { id: toastId })
    } finally {
      setDownloadingFolder(null)
    }
  }

  const isLoading = statsLoading || clientsLoading

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-6 w-40" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    )
  }

  const libraryFolders = mediaData?.folders?.filter(f => f.type === 'library') ?? []
  const cutsFolders = mediaData?.folders?.filter(f => f.type === 'cuts') ?? []
  const totalImages = mediaData?.folders?.reduce((sum, f) => sum + f.imageCount, 0) ?? 0

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            <h1 className="text-xl font-bold tracking-tight">Admin</h1>
          </div>
          <p className="text-sm text-muted-foreground">{t('Painel administrativo do sistema')}</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-1.5"><UserPlus className="h-3.5 w-3.5" /> {t('Novo Cliente')}</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t('Novo Cliente')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="mb-1.5 text-xs text-muted-foreground">{t('Nome')}</Label>
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t('Nome do cliente')} className="h-9 text-sm" />
              </div>
              <div>
                <Label className="mb-1.5 text-xs text-muted-foreground">Username</Label>
                <Input value={newUsername} onChange={(e) => setNewUsername(e.target.value)} placeholder="usuario (sem espacos)" className="h-9 text-sm" />
              </div>
              <div>
                <Label className="mb-1.5 text-xs text-muted-foreground">{t('Senha (min 6 chars)')}</Label>
                <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="******" className="h-9 text-sm" />
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={restrictAccess} onChange={(e) => setRestrictAccess(e.target.checked)} className="h-4 w-4 rounded border-border accent-primary" />
                  <span className="text-sm flex items-center gap-1.5">
                    <Lock className="h-3.5 w-3.5" /> {t('Restringir acesso')}
                  </span>
                </label>
                {restrictAccess && (
                  <div className="grid grid-cols-2 gap-1.5 rounded-lg border border-border p-3 max-h-48 overflow-y-auto">
                    {ALL_PAGES.map((page) => (
                      <label key={page.value} className="flex items-center gap-1.5 text-xs cursor-pointer py-0.5">
                        <input
                          type="checkbox"
                          checked={selectedPages.includes(page.value)}
                          onChange={(e) => {
                            setSelectedPages(prev =>
                              e.target.checked ? [...prev, page.value] : prev.filter(p => p !== page.value)
                            )
                          }}
                          className="h-3.5 w-3.5 rounded border-border accent-primary"
                        />
                        {page.label}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <Button onClick={handleCreate} disabled={createClient.isPending} className="w-full">
                {createClient.isPending ? t('Criando...') : t('Criar Cliente')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Maintenance Mode Toggle */}
      <div className="flex items-center justify-between rounded-xl border border-border bg-card px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/10">
            <AlertTriangle className="h-4.5 w-4.5 text-amber-500" />
          </div>
          <div>
            <h2 className="text-sm font-semibold">{t('Modo Manutencao')}</h2>
            <p className="text-[11px] text-muted-foreground">{t('Exibe um banner de aviso para todos os usuarios')}</p>
          </div>
        </div>
        <Switch
          checked={systemSettings?.maintenance_mode ?? false}
          onCheckedChange={(checked) => {
            toggleMaintenance.mutate(checked, {
              onSuccess: () => toast.success(checked ? t('Manutencao ativada') : t('Manutencao desativada')),
              onError: () => toast.error(t('Erro ao atualizar')),
            })
          }}
          disabled={toggleMaintenance.isPending}
        />
      </div>

      {/* Apps Meta (App ID + Chave Secreta + token) */}
      <div className="rounded-xl border border-border bg-card px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-500/10">
            <Lock className="h-4.5 w-4.5 text-blue-500" />
          </div>
          <div>
            <h2 className="text-sm font-semibold">{t('Apps do Meta (API)')}</h2>
            <p className="text-[11px] text-muted-foreground">
              {t('Aponte cada app aqui (App ID + Chave Secreta). Caiu/baniu um? Troque pelo painel — sem SSH. Postagem e broadcast são separados.')}
            </p>
          </div>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          {(['posting', 'broadcast'] as const).map((app) => {
            const info = metaApi?.apps?.[app]
            const label = app === 'posting' ? t('Postagem') : t('Broadcast')
            return (
              <div key={app} className="rounded-lg border border-border bg-background/50 p-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold">{label}</span>
                  {info?.status === 'ok' && (
                    <Badge className="gap-1 bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/15"><CheckCircle2 className="h-3 w-3" />{t('Conectado')}</Badge>
                  )}
                  {info?.status === 'down' && (
                    <Badge className="gap-1 bg-red-500/15 text-red-600 hover:bg-red-500/15"><AlertTriangle className="h-3 w-3" />{t('API caiu')}</Badge>
                  )}
                  {(!info || info.status === 'unset') && (
                    <Badge variant="outline" className="text-muted-foreground">{t('Sem token')}</Badge>
                  )}
                </div>

                <div className="mt-3 space-y-2">
                  <div>
                    <Label className="text-[11px] text-muted-foreground">{t('App ID')}{info?.from_panel ? '' : ` (${t('do .env')})`}</Label>
                    <Input
                      value={metaForm[app].app_id}
                      onChange={(e) => setMetaForm((s) => ({ ...s, [app]: { ...s[app], app_id: e.target.value } }))}
                      placeholder={info?.app_id || t('Cole o App ID')}
                      className="mt-1 font-mono text-xs"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] text-muted-foreground">{t('Chave Secreta')}</Label>
                    <Input
                      type="password"
                      value={metaForm[app].app_secret}
                      onChange={(e) => setMetaForm((s) => ({ ...s, [app]: { ...s[app], app_secret: e.target.value } }))}
                      placeholder={info?.has_secret ? t('Salva — cole nova pra trocar') : t('Cole a Chave Secreta')}
                      className="mt-1 font-mono text-xs"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] text-muted-foreground">{t('Token de acesso (opcional)')}</Label>
                    <Input
                      type="password"
                      value={metaForm[app].token}
                      onChange={(e) => setMetaForm((s) => ({ ...s, [app]: { ...s[app], token: e.target.value } }))}
                      placeholder={info?.has_token ? `${t('Salvo')}: ${info.preview ?? ''} — ${t('cole novo pra trocar')}` : t('Token que vale pra todas as páginas')}
                      className="mt-1 font-mono text-xs"
                    />
                  </div>
                </div>

                <div className="mt-3 flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => saveMetaApp.mutate(app, {
                      onSuccess: () => toast.success(t('Salvo')),
                      onError: () => toast.error(t('Erro ao salvar')),
                    })}
                    disabled={saveMetaApp.isPending || (metaForm[app].app_id.trim() === '' && metaForm[app].app_secret.trim() === '' && metaForm[app].token.trim() === '')}
                  >
                    {saveMetaApp.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('Salvar')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => testMetaApp.mutate(app, {
                      onSuccess: (r: any) => r?.status === 'ok' ? toast.success(`${t('Conectado')}${r?.name ? ' — ' + r.name : ''}`) : toast.error(r?.error || t('API caiu')),
                      onError: () => toast.error(t('Falha ao testar')),
                    })}
                    disabled={testMetaApp.isPending || !info?.has_token}
                  >
                    {testMetaApp.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('Testar')}
                  </Button>
                </div>

                {info?.status === 'down' && info.error && (
                  <p className="mt-2 text-[11px] text-red-500 break-words">{info.error}</p>
                )}
                {info?.checked_at && (
                  <p className="mt-2 text-[11px] text-muted-foreground">{t('Última verificação')}: {new Date(info.checked_at).toLocaleString('pt-BR')}</p>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Webhook — info pra colar no Meta App Dashboard */}
      <div className="rounded-xl border border-border bg-card px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-purple-500/10">
            <Shield className="h-4.5 w-4.5 text-purple-500" />
          </div>
          <div>
            <h2 className="text-sm font-semibold">{t('Webhook (colar no Meta)')}</h2>
            <p className="text-[11px] text-muted-foreground">
              {t('No Meta App Dashboard → Webhooks, cole a URL de callback e o Verify token abaixo.')}
            </p>
          </div>
        </div>

        <div className="mt-4 space-y-2">
          <div>
            <Label className="text-[11px] text-muted-foreground">{t('URL de callback')}</Label>
            <div className="mt-1 flex gap-2">
              <Input readOnly value={metaApi?.webhook?.callback_url ?? ''} className="flex-1 font-mono text-xs" />
              <Button size="sm" variant="outline" onClick={() => copyText(t('URL de callback'), metaApi?.webhook?.callback_url)}><Copy className="h-4 w-4" /></Button>
            </div>
          </div>
          <div>
            <Label className="text-[11px] text-muted-foreground">{t('Verify token')}</Label>
            <div className="mt-1 flex gap-2">
              <Input readOnly value={metaApi?.webhook?.verify_token ?? ''} className="flex-1 font-mono text-xs" />
              <Button size="sm" variant="outline" onClick={() => copyText(t('Verify token'), metaApi?.webhook?.verify_token)}><Copy className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>
      </div>

      {/* Workers / Filas — suporte resolve B.O. sem SSH */}
      <div className="rounded-xl border border-border bg-card px-5 py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-orange-500/10">
              <Server className="h-4.5 w-4.5 text-orange-500" />
            </div>
            <div>
              <h2 className="text-sm font-semibold">{t('Workers / Filas')}</h2>
              <p className="text-[11px] text-muted-foreground">
                {t('Fila travada ou posts não saindo? Reinicie os workers ou limpe os travados aqui.')}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => restartWorkers.mutate(undefined, {
                onSuccess: () => toast.success(t('Workers reiniciando...')),
                onError: () => toast.error(t('Erro ao reiniciar')),
              })}
              disabled={restartWorkers.isPending || clearWorkers.isPending}
            >
              {restartWorkers.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="ml-1.5">{t('Reiniciar')}</span>
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-red-600 hover:text-red-600"
              onClick={() => {
                if (!confirm(t('Limpar jobs travados e falhados? Isso desbloqueia a fila.'))) return
                clearWorkers.mutate(undefined, {
                  onSuccess: (r: any) => toast.success(`${t('Limpo')}: ${r?.stuck_removed ?? 0} ${t('travados')}, ${r?.failed_removed ?? 0} ${t('falhados')}`),
                  onError: () => toast.error(t('Erro ao limpar')),
                })
              }}
              disabled={restartWorkers.isPending || clearWorkers.isPending}
            >
              {clearWorkers.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              <span className="ml-1.5">{t('Limpar travados')}</span>
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => {
                if (!confirm(t('FORÇAR restart mata e reinicia TODOS os workers na hora. Use se algum estiver pendurado. Continuar?'))) return
                forceRestart.mutate(undefined, {
                  onSuccess: (r: any) => r?.ok ? toast.success(t('Workers reiniciados à força')) : toast.error(r?.output || t('Falha ao forçar restart')),
                  onError: () => toast.error(t('Falha ao forçar restart')),
                })
              }}
              disabled={restartWorkers.isPending || clearWorkers.isPending || forceRestart.isPending}
            >
              {forceRestart.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
              <span className="ml-1.5">{t('Forçar restart')}</span>
            </Button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-3">
          <div className="rounded-lg border border-border bg-background/50 p-3 text-center">
            <p className="text-2xl font-bold">{workers?.pending ?? '—'}</p>
            <p className="text-[11px] text-muted-foreground">{t('Na fila')}</p>
          </div>
          <div className={`rounded-lg border p-3 text-center ${(workers?.stuck ?? 0) > 0 ? 'border-red-500/40 bg-red-500/5' : 'border-border bg-background/50'}`}>
            <p className={`text-2xl font-bold ${(workers?.stuck ?? 0) > 0 ? 'text-red-600' : ''}`}>{workers?.stuck ?? '—'}</p>
            <p className="text-[11px] text-muted-foreground">{t('Travados')}</p>
          </div>
          <div className="rounded-lg border border-border bg-background/50 p-3 text-center">
            <p className={`text-2xl font-bold ${(workers?.failed ?? 0) > 0 ? 'text-amber-600' : ''}`}>{workers?.failed ?? '—'}</p>
            <p className="text-[11px] text-muted-foreground">{t('Falhados')}</p>
          </div>
        </div>

        {(workers?.stuck ?? 0) > 0 && (
          <p className="mt-3 text-[11px] text-red-500">{t('Há jobs travados (em loop). Clique "Limpar travados" pra desbloquear a fila.')}</p>
        )}
      </div>

      {/* Ações de suporte */}
      <div className="rounded-xl border border-border bg-card px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-500/10">
            <Wrench className="h-4.5 w-4.5 text-indigo-500" />
          </div>
          <div>
            <h2 className="text-sm font-semibold">{t('Ações de suporte')}</h2>
            <p className="text-[11px] text-muted-foreground">
              {t('Conserta os B.O. mais comuns. Use a consulta abaixo pra achar o ID quando precisar.')}
            </p>
          </div>
        </div>

        <div className="mt-4 space-y-2">
          {/* Revalidar tokens */}
          <div className="flex flex-col gap-2 rounded-lg border border-border bg-background/50 p-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-medium">{t('Token caiu')}</p>
              <p className="text-[11px] text-muted-foreground">{t('Revalida todos os tokens no Graph e corrige os que voltaram a funcionar.')}</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => revalidateTokens.mutate()}
              disabled={revalidateTokens.isPending}
            >
              {revalidateTokens.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="ml-1.5">{t('Revalidar tokens')}</span>
            </Button>
          </div>

          {/* Reprocessar post */}
          <div className="flex flex-col gap-2 rounded-lg border border-border bg-background/50 p-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium">{t('Post travou')}</p>
              <p className="text-[11px] text-muted-foreground">{t('Recoloca o post na fila — publica no próximo ciclo (até 1 min).')}</p>
            </div>
            <div className="flex gap-2">
              <Input
                value={postId}
                onChange={(e) => setPostId(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && postId.trim()) reprocessPost.mutate() }}
                placeholder={t('ID do post')}
                className="w-32 font-mono text-xs"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => reprocessPost.mutate()}
                disabled={reprocessPost.isPending || postId.trim() === ''}
              >
                {reprocessPost.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                <span className="ml-1.5">{t('Reprocessar')}</span>
              </Button>
            </div>
          </div>

          {/* Reprocessar broadcast */}
          <div className="flex flex-col gap-2 rounded-lg border border-border bg-background/50 p-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium">{t('Broadcast emperrou')}</p>
              <p className="text-[11px] text-muted-foreground">{t('Retoma o envio de onde parou — sem reenviar quem já recebeu.')}</p>
            </div>
            <div className="flex gap-2">
              <Input
                value={broadcastId}
                onChange={(e) => setBroadcastId(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && broadcastId.trim()) reprocessBroadcast.mutate() }}
                placeholder={t('ID do broadcast')}
                className="w-32 font-mono text-xs"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => reprocessBroadcast.mutate()}
                disabled={reprocessBroadcast.isPending || broadcastId.trim() === ''}
              >
                {reprocessBroadcast.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                <span className="ml-1.5">{t('Reprocessar')}</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Consulta ao banco (só leitura) */}
      <div className="rounded-xl border border-border bg-card px-5 py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/10">
              <Database className="h-4.5 w-4.5 text-emerald-500" />
            </div>
            <div>
              <h2 className="text-sm font-semibold">{t('Consulta ao banco')}</h2>
              <p className="text-[11px] text-muted-foreground">
                {t('Diagnostique qualquer coisa com SELECT. Escrita bloqueada (usuário só-leitura no banco).')}
              </p>
            </div>
          </div>
          <Badge className="gap-1 bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/15"><Lock className="h-3 w-3" />{t('Só leitura')}</Badge>
        </div>

        <div className="mt-3 space-y-1.5">
          {[
            { group: t('Páginas & Tokens'), items: [
              { label: t('Status das páginas'), q: 'SELECT id, name, username, page_id, ig_user_id, is_active, fb_active, fb_only, token_status, token_verified_at FROM accounts ORDER BY id DESC LIMIT 100' },
              { label: t('Tokens inválidos'), q: 'SELECT t.account_id, a.name, t.profile_name, t.app_key, t.is_valid, t.added_at FROM account_tokens t JOIN accounts a ON a.id = t.account_id WHERE t.is_valid = 0 ORDER BY t.added_at DESC LIMIT 100' },
              { label: t('Tokens por app'), q: 'SELECT app_key, is_valid, COUNT(*) total FROM account_tokens GROUP BY app_key, is_valid' },
              { label: t('Contas sem token válido'), q: 'SELECT a.id, a.name, a.username, a.token_status FROM accounts a WHERE NOT EXISTS (SELECT 1 FROM account_tokens t WHERE t.account_id = a.id AND t.is_valid = 1) ORDER BY a.id DESC LIMIT 100' },
            ]},
            { group: t('Posts'), items: [
              { label: t('Recentes'), q: 'SELECT id, title, page_id, account_name, status, scheduled_at, published_at FROM scheduled_posts ORDER BY id DESC LIMIT 50' },
              { label: t('Falhados'), q: "SELECT id, title, page_id, account_name, status, error_message, scheduled_at FROM scheduled_posts WHERE status = 'failed' OR error_message IS NOT NULL ORDER BY id DESC LIMIT 50" },
              { label: t('Atrasados'), q: 'SELECT id, title, page_id, account_name, status, scheduled_at FROM scheduled_posts WHERE is_published = 0 AND scheduled_at <= NOW() ORDER BY scheduled_at ASC LIMIT 50' },
              { label: t('Por status'), q: 'SELECT status, COUNT(*) total FROM scheduled_posts GROUP BY status ORDER BY total DESC' },
            ]},
            { group: t('Broadcast'), items: [
              { label: t('Recentes'), q: 'SELECT id, user_id, status, sent_count, failed_count, total_contacts, scheduled_at, created_at FROM broadcast_history ORDER BY id DESC LIMIT 50' },
              { label: t('Travados'), q: "SELECT id, user_id, status, sent_count, total_contacts, created_at FROM broadcast_history WHERE status = 'sending' ORDER BY created_at DESC LIMIT 50" },
            ]},
            { group: t('Sequências'), items: [
              { label: t('Ativas'), q: 'SELECT sequence_id, platform, COUNT(*) total FROM sequence_queue WHERE is_completed = 0 GROUP BY sequence_id, platform ORDER BY total DESC LIMIT 50' },
              { label: t('Próximos envios'), q: 'SELECT id, sequence_id, page_id, platform, current_step, next_send_at FROM sequence_queue WHERE is_completed = 0 AND next_send_at IS NOT NULL ORDER BY next_send_at ASC LIMIT 50' },
            ]},
            { group: t('Contatos & Agente'), items: [
              { label: t('Contatos por página'), q: 'SELECT page_id, COUNT(*) total, SUM(is_dead) mortos FROM contacts GROUP BY page_id ORDER BY total DESC LIMIT 50' },
              { label: t('Erros do agente'), q: 'SELECT id, user_id, error_code, page_name, group_name, created_at FROM agent_error_logs ORDER BY id DESC LIMIT 50' },
              { label: t('Erros por código'), q: 'SELECT error_code, COUNT(*) total FROM agent_error_logs GROUP BY error_code ORDER BY total DESC LIMIT 30' },
            ]},
            { group: t('Sistema'), items: [
              { label: t('Tabelas'), q: "SELECT table_name, table_rows FROM information_schema.tables WHERE table_schema = 'conectagran_v2' ORDER BY table_rows DESC" },
              { label: t('Usuários'), q: 'SELECT id, name, username, is_admin, max_pages, trial_ends_at, created_at FROM users ORDER BY id DESC LIMIT 50' },
              { label: t('Jobs falhados por tipo'), q: `SELECT SUBSTRING_INDEX(SUBSTRING_INDEX(payload, '"displayName":"', -1), '"', 1) AS job, COUNT(*) total FROM failed_jobs GROUP BY job ORDER BY total DESC LIMIT 30` },
            ]},
          ].map((g) => (
            <div key={g.group} className="flex flex-wrap items-center gap-1.5">
              <span className="w-full text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/60 sm:w-[130px]">{g.group}</span>
              {g.items.map((ex) => (
                <button key={ex.label} onClick={() => setSql(ex.q)} className="rounded-md border border-border bg-background px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground hover:border-emerald-500/40">
                  {ex.label}
                </button>
              ))}
            </div>
          ))}
        </div>

        <textarea
          value={sql}
          onChange={(e) => setSql(e.target.value)}
          onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') runQuery.mutate() }}
          placeholder="SELECT * FROM users WHERE email = '...'"
          rows={3}
          className="mt-2 w-full resize-y rounded-lg border border-border bg-background p-3 font-mono text-xs outline-none focus:border-emerald-500/50"
        />

        <div className="mt-2 flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => runQuery.mutate()}
            disabled={runQuery.isPending || sql.trim() === ''}
          >
            {runQuery.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            <span className="ml-1.5">{t('Rodar')}</span>
          </Button>
          <span className="text-[11px] text-muted-foreground">{t('⌘/Ctrl + Enter pra rodar. Máx 500 linhas.')}</span>
          {dbResult && <span className="ml-auto text-[11px] text-muted-foreground">{dbResult.count} {t('linha(s)')}</span>}
        </div>

        {dbResult && dbResult.rows.length > 0 && (
          <div className="mt-3 max-h-80 overflow-auto rounded-lg border border-border">
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                <tr>{dbResult.columns.map((c) => <th key={c} className="whitespace-nowrap px-3 py-2 font-semibold">{c}</th>)}</tr>
              </thead>
              <tbody>
                {dbResult.rows.map((row, i) => (
                  <tr key={i} className="border-t border-border hover:bg-muted/30">
                    {dbResult.columns.map((c) => {
                      const v = row[c]
                      const s = v === null ? 'NULL' : typeof v === 'object' ? JSON.stringify(v) : String(v)
                      return <td key={c} className={`max-w-[280px] truncate px-3 py-1.5 font-mono ${v === null ? 'text-muted-foreground/50 italic' : ''}`} title={s}>{s}</td>
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {dbResult && dbResult.rows.length === 0 && (
          <p className="mt-3 text-[11px] text-muted-foreground">{t('Nenhuma linha retornada.')}</p>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <StatCard label={t('Clientes')} value={stats.totalClients} icon={Users} color="blue" />
          <StatCard label={t('Contas totais')} value={stats.totalAccounts} icon={Zap} color="purple" />
          <StatCard label={t('IG Ativas')} value={stats.totalActiveIG} icon={Instagram} color="purple" />
          <StatCard label={t('FB Ativas')} value={stats.totalActiveFB} icon={Facebook} color="blue" />
          <StatCard label={t('DMs IG (7d)')} value={stats.totalDMsIG} icon={MessageCircle} color="green" />
        </div>
      )}

      {stats && (
        <div className="grid gap-4 sm:grid-cols-4">
          <div className="rounded-xl border border-border bg-card p-4 text-center">
            <div className="text-lg font-bold text-blue-600 dark:text-blue-400">{stats.totalDMsFB}</div>
            <div className="text-[10px] text-muted-foreground">{t('DMs FB (7d)')}</div>
          </div>
          <div className="rounded-xl border border-border bg-card p-4 text-center">
            <div className="text-lg font-bold">{stats.contactStats.total}</div>
            <div className="text-[10px] text-muted-foreground">{t('Contatos')}</div>
          </div>
          <div className="rounded-xl border border-border bg-card p-4 text-center">
            <div className="text-lg font-bold text-primary">{stats.contactStats.within24h}</div>
            <div className="text-[10px] text-muted-foreground">{t('Janela 24h')}</div>
          </div>
          <div className="rounded-xl border border-border bg-card p-4 text-center">
            <div className="text-lg font-bold text-purple-600 dark:text-purple-400">{stats.otnAvailable}/{stats.otnTotal}</div>
            <div className="text-[10px] text-muted-foreground">{t('OTN Disponiveis')}</div>
          </div>
        </div>
      )}

      {/* Clients table */}
      <div className="overflow-hidden rounded-xl border border-border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="text-[10px] font-semibold uppercase tracking-wider">{t('Cliente')}</TableHead>
              <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-center">{t('Contas')}</TableHead>
              <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-center">{t('IG Ativas')}</TableHead>
              <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-center">{t('FB Ativas')}</TableHead>
              <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-right">{t('Acao')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!clients?.length ? (
              <TableRow>
                <TableCell colSpan={5} className="py-12 text-center">
                  <Users className="mx-auto mb-2 h-8 w-8 text-muted-foreground/20" />
                  <p className="text-xs text-muted-foreground">{t('Nenhum cliente cadastrado')}</p>
                </TableCell>
              </TableRow>
            ) : (
              clients.map((client) => (
                <TableRow key={client.id}>
                  <TableCell>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium">{client.name}</span>
                        {client.allowedPages && (
                          <Badge variant="outline" className="text-[9px] gap-0.5 px-1 py-0 text-amber-500 border-amber-500/30">
                            <Lock className="h-2.5 w-2.5" /> Restrito
                          </Badge>
                        )}
                      </div>
                      <div className="text-[10px] text-muted-foreground">@{client.username}</div>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="outline" className="text-xs">{client.accountCount}</Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={client.igActive > 0 ? 'default' : 'secondary'} className="text-xs">
                      {client.igActive}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={client.fbActive > 0 ? 'default' : 'secondary'} className={`text-xs ${client.fbActive > 0 ? 'bg-blue-500 text-white' : ''}`}>
                      {client.fbActive}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-primary"
                        title={t('Biblioteca')}
                        onClick={() => {
                          setLibraryClientId(client.id)
                          setLibraryClientName(client.name)
                        }}
                      >
                        <FolderOpen className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => handleDelete(client.id, client.name)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Client Library Dialog */}
      <Dialog open={!!libraryClientId} onOpenChange={(open) => { if (!open) { setLibraryClientId(null); setCopySourceId(''); setCopyProgress(null); setCopyDone(null) } }}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="h-4 w-4" />
              Biblioteca — {libraryClientName}
            </DialogTitle>
          </DialogHeader>

          {mediaLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto space-y-4">
              <div className="text-xs text-muted-foreground">
                {mediaData?.folders?.length ?? 0} pasta(s) — {totalImages} arquivo(s)
              </div>

              {/* Copy media section */}
              <div className="rounded-lg border border-dashed border-border p-3 space-y-2">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                  <Copy className="h-3 w-3" /> Copiar Midia de Outro Usuario
                </div>
                <div className="flex items-center gap-2">
                  <select
                    className="flex-1 h-8 rounded-md border border-border bg-background px-2 text-xs"
                    value={copySourceId}
                    onChange={(e) => setCopySourceId(e.target.value)}
                    disabled={!!copyProgress}
                  >
                    <option value="">Selecionar origem...</option>
                    {allUsers?.filter(u => u.id !== libraryClientId).map(u => (
                      <option key={u.id} value={u.id}>@{u.username} — {u.name}</option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    className="gap-1.5 h-8 text-xs shrink-0"
                    disabled={!copySourceId || !!copyProgress}
                    onClick={handleCopyMedia}
                  >
                    {copyProgress ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Copy className="h-3 w-3" />
                    )}
                    Copiar
                  </Button>
                </div>
                {copyProgress && (
                  <div className="text-[11px] text-muted-foreground animate-pulse">
                    {copyProgress.phase === 'init' && 'Carregando pastas...'}
                    {copyProgress.phase === 'folders_done' && `${copyProgress.foldersCopied} pastas criadas, copiando arquivos...`}
                    {copyProgress.phase === 'copying' && `${copyProgress.copied}/${copyProgress.total} copiados (${copyProgress.errors} erros)`}
                  </div>
                )}
                {copyDone && (
                  <div className="text-[11px] text-green-600 dark:text-green-400 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    {copyDone.copied} copiados, {copyDone.foldersCopied} pastas
                    {copyDone.errors > 0 && ` (${copyDone.errors} erros — arquivos ja deletados no R2)`}
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground/60">
                  Copia real: cada arquivo e duplicado no R2 com path proprio
                </div>
              </div>

              {libraryFolders.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Biblioteca</div>
                  {libraryFolders.map((folder) => (
                    <div key={folder.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <FolderOpen className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{folder.name}</div>
                          <div className="text-[10px] text-muted-foreground">{folder.imageCount} arquivo(s)</div>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5 shrink-0 h-7 text-xs"
                        disabled={folder.imageCount === 0 || downloadingFolder === folder.id}
                        onClick={() => handleDownloadFolder(folder.id, folder.name)}
                      >
                        {downloadingFolder === folder.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Download className="h-3 w-3" />
                        )}
                        ZIP
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {cutsFolders.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Cortes</div>
                  {cutsFolders.map((folder) => (
                    <div key={folder.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <FolderOpen className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{folder.name}</div>
                          <div className="text-[10px] text-muted-foreground">{folder.imageCount} arquivo(s)</div>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5 shrink-0 h-7 text-xs"
                        disabled={folder.imageCount === 0 || downloadingFolder === folder.id}
                        onClick={() => handleDownloadFolder(folder.id, folder.name)}
                      >
                        {downloadingFolder === folder.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Download className="h-3 w-3" />
                        )}
                        ZIP
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {(!mediaData?.folders?.length) && (
                <div className="flex flex-col items-center gap-2 py-8 text-center">
                  <FolderOpen className="h-10 w-10 text-muted-foreground/15" />
                  <p className="text-xs text-muted-foreground">Nenhuma pasta encontrada</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
